import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type ClientListEntry, type Job, type JobStatus, type Me } from "../lib/api";
import { JobForm } from "./JobForm";
import { formatFee, formatSalary, JOB_STATUS_LABEL } from "../lib/format";

const pad = (n: number) => String(n).padStart(2, "0");

export function JobStatusChip({ status }: { status: JobStatus }) {
  const tone = status === "open" ? "" : status === "on_hold" ? "pending" : "off";
  return <span className={`status ${tone}`}>{JOB_STATUS_LABEL[status]}</span>;
}

/** One job as a ledger row; used on the Jobs page and on company pages. */
export function JobRow({ job, index, showCompany, showOwner }: { job: Job; index: number; showCompany: boolean; showOwner: boolean }) {
  const facts = [job.location, formatSalary(job.salaryMin, job.salaryMax, job.currency), `${formatFee(job.fee, job.currency)} fee`].filter(Boolean);
  return (
    <li className={job.status === "lost" ? "inactive" : undefined}>
      <span className="num" aria-hidden>
        {pad(index + 1)}
      </span>
      <span className="main">
        <span className="primary">
          <Link to={`/jobs/${job.id}`}>{job.title}</Link>
          {showCompany && (
            <span className="secondary">
              {" · "}
              <Link to={`/companies/${job.company.id}`}>{job.company.name}</Link>
            </span>
          )}
        </span>
        <span className="secondary">
          {facts.join(" · ")}
          {showOwner && <> · {job.owner.name}</>}
        </span>
        <span className="secondary">
          {job.inProcess} in process{job.placed > 0 && <> · {job.placed} placed</>}
        </span>
      </span>
      <span className="actions">
        <JobStatusChip status={job.status} />
      </span>
    </li>
  );
}

const FILTERS: { value: JobStatus | "all"; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "on_hold", label: "On hold" },
  { value: "filled", label: "Filled" },
  { value: "lost", label: "Lost" },
  { value: "all", label: "All" },
];

export function Jobs({ me }: { me: Me }) {
  const [status, setStatus] = useState<JobStatus | "all">("open");
  const [owner, setOwner] = useState<"me" | "all">("all");
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [adding, setAdding] = useState(false);
  const load = useAction();

  useEffect(() => {
    setJobs(null);
    void load.run(async () => setJobs((await api.jobs({ status: status === "all" ? undefined : status, owner })).jobs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, owner]);

  return (
    <>
      <div className="page-head">
        <p className="label">{me.tenant.name}</p>
        <h1 className="display">
          Roles you're <em>working</em>.
        </h1>
        <p className="lede">Everyone on the team sees every job and who owns it, so no one pitches for a role a colleague already has.</p>
      </div>

      {adding && <NewJob onCancel={() => setAdding(false)} />}

      <div className="section-head">
        <div className="tabs" role="group" aria-label="Job status">
          {FILTERS.map((f) => (
            <button key={f.value} type="button" className="tab" aria-pressed={status === f.value} onClick={() => setStatus(f.value)}>
              {f.label}
            </button>
          ))}
        </div>
        <span className="button-row">
          <div className="tabs" role="group" aria-label="Whose jobs">
            {(["all", "me"] as const).map((o) => (
              <button key={o} type="button" className="tab" aria-pressed={owner === o} onClick={() => setOwner(o)}>
                {o === "me" ? "Mine" : "Everyone"}
              </button>
            ))}
          </div>
          {!adding && (
            <button type="button" className="link-btn" onClick={() => setAdding(true)}>
              + New job
            </button>
          )}
        </span>
      </div>

      <ErrorNotice error={load.error} />
      <ol className="index">
        {jobs?.map((j, i) => <JobRow key={j.id} job={j} index={i} showCompany showOwner={owner === "all"} />)}
        {jobs && jobs.length === 0 && (
          <li>
            <span className="empty">
              No {status === "all" ? "" : JOB_STATUS_LABEL[status].toLowerCase() + " "}jobs yet.
            </span>
          </li>
        )}
        {!jobs && !load.error && (
          <li>
            <span className="empty">Loading…</span>
          </li>
        )}
      </ol>
    </>
  );
}

/** A new job from the Jobs page: pick the company first, then the usual job form (fee from its terms). */
function NewJob({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<ClientListEntry[] | null>(null);
  const [companyId, setCompanyId] = useState("");
  const load = useAction();
  useEffect(() => {
    void load.run(async () => setCompanies((await api.clients()).clients));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const sorted = [...(companies ?? [])].sort(
    (a, b) => (a.client?.status === b.client?.status ? a.name.localeCompare(b.name) : a.client?.status === "client" ? -1 : 1),
  );
  return (
    <section className="section new-job" aria-labelledby="new-job-h">
      <div className="section-head">
        <h2 id="new-job-h" className="label">
          New job
        </h2>
        <button type="button" className="link-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <ErrorNotice error={load.error} />
      <div className="form narrow">
        <label className="field">
          <span className="label">Company</span>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} disabled={!companies}>
            <option value="">{companies ? "Choose a company on your list" : "Loading…"}</option>
            {sorted.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.client?.status === "lead" ? " (lead)" : ""}
              </option>
            ))}
          </select>
          <span className="hint">
            Not listed? <Link to="/companies">Add the company first</Link>, then come back.
          </span>
        </label>
      </div>
      {companyId && (
        <JobForm
          key={companyId}
          companyId={companyId}
          submitLabel="Create job"
          onCancel={onCancel}
          onSubmit={async (input) => {
            const { job } = await api.createJob(companyId, input);
            navigate(`/jobs/${job.id}`);
          }}
        />
      )}
    </section>
  );
}
